import { useCallback } from 'react';
import {
  EntityListTypeSupported,
  getEntityListSpecification,
  IMPORT_STEPS,
} from '../entityListsLib';
import { resetImportForAllTypes } from '../entityListAdministrationSlice';
import { addToast } from '@ydistri/utils';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../../store';
import { useCurrentEntityListState } from './useCurrentEntityListState';
import { useSetImportStepRunning } from './useSetImportStepRunning';
import { useDeleteEntityListImportMutation } from '../apiEntityLists';
import { QueryStatus } from '@reduxjs/toolkit/query';

/**
 * Returns a function to delete the import of an entity list and accompanying state.
 * Local state must be reset after entity list import is deleted.
 * @param entityListType
 * @param onSettled runs with the list's id once the deletion has succeeded or failed
 */
const useDeleteEntityListImport = (
  entityListType: EntityListTypeSupported,
  onSettled?: (entityListId: number) => void,
): [() => void, QueryStatus] => {
  const setStepStatus = useSetImportStepRunning(entityListType);
  const dispatch = useAppDispatch();
  const [deleteEntityListImport, { status: deleteStatus }] = useDeleteEntityListImportMutation();

  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );

  const entityListState = useCurrentEntityListState(entityListType);

  const entityListId = selectedEntityList?.entityListId;
  const verificationTimeStamp = entityListState?.verificationTimeStamp;
  const entityListSpecification = getEntityListSpecification(entityListType);

  const deleteHandler = useCallback(() => {
    if (entityListId && verificationTimeStamp) {
      setStepStatus(IMPORT_STEPS.ITEM_VALIDATION, true);
      deleteEntityListImport({
        entityListId,
        entityListType,
        verificationTimeStamp,
      })
        .unwrap()
        .then(() => {
          dispatch(resetImportForAllTypes({ entityListId }));
          dispatch(
            addToast({
              isError: false,
              message: `Import of ${entityListSpecification.title} was canceled.`,
            }),
          );
        })
        .catch(() => {
          dispatch(
            addToast({
              isError: true,
              message: `Cancellation of ${entityListSpecification.title} import was unsuccessful. Please refresh the page and try again or contact the administrator.`,
            }),
          );
        })
        .finally(() => {
          setStepStatus(IMPORT_STEPS.ITEM_VALIDATION, false);
          onSettled?.(entityListId);
        });
    }
  }, [
    deleteEntityListImport,
    dispatch,
    entityListId,
    entityListSpecification.title,
    entityListType,
    onSettled,
    setStepStatus,
    verificationTimeStamp,
  ]);

  return [deleteHandler, deleteStatus];
};

export default useDeleteEntityListImport;
